import { CalendarDays, Mail, MapPin, Phone } from "lucide-react";
import CtaPanel from "../../components/sections/CtaPanel";
import PageHero from "../../components/sections/PageHero";
import { ButtonLink } from "../../components/ui/Button";
import ExternalLink from "../../components/ui/ExternalLink";
import { engagementSteps } from "../../data/services";
import { images } from "../../data/images";
import { company, defaultBookingUrl, routes } from "../../data/site";
import { localHref } from "../../lib/links";
import ContactForm from "./ContactForm";

// Based on the live Contact Us page.
const contactIntro =
  "Whether it's a quick question or a deeper conversation, we take everyone's time seriously and make sure it's well spent. Clients count on our prompt communication and our knack for digging down to the root cause.";
const mapEmbed =
  "https://maps.google.com/maps?q=xeratec%20hancock%20mi&t=m&z=10&output=embed&iwloc=near";

function Detail({ icon: Icon, label, children, href, external }) {
  const Tag = external ? ExternalLink : "a";
  return (
    <li>
      <Tag
        href={href}
        className="group flex items-start gap-4 rounded-brand p-3 transition-colors hover:bg-white/6"
      >
        <span className="grid size-11 shrink-0 place-items-center rounded-brand bg-yellow text-ink">
          <Icon size={20} strokeWidth={2} aria-hidden="true" />
        </span>
        <span className="min-w-0">
          <span className="tag block text-white/60">{label}</span>
          <span className="mt-0.5 block text-[17px] font-bold wrap-break-word text-white transition-colors group-hover:text-yellow">
            {children}
          </span>
        </span>
      </Tag>
    </li>
  );
}

/** Phone, email and address on a blueprint card, with the map below. */
function ContactDetails() {
  const { address } = company;
  return (
    <div className="flex flex-col gap-6">
      <div className="blueprint rounded-brand p-5 text-white shadow-lift sm:p-6">
        <h2 className="eyebrow mb-4 px-3 pt-2 text-blue-soft">
          Reach us directly
        </h2>
        <ul className="space-y-1">
          <Detail icon={Phone} label="Call" href={company.phone.href}>
            {company.phone.display}
          </Detail>
          <Detail icon={Mail} label="Email" href={`mailto:${company.email}`}>
            {company.email}
          </Detail>
          <Detail
            icon={MapPin}
            label="Michigan headquarters"
            href={company.mapsUrl}
            external
          >
            {address.street}
            <br />
            {address.city}, {address.region} {address.postalCode}
          </Detail>
        </ul>
      </div>
      <iframe
        title="Map of Xeratec in Hancock, Michigan"
        src={mapEmbed}
        loading="lazy"
        className="h-64 w-full rounded-brand border border-line bg-surface shadow-soft"
      />
    </div>
  );
}

export function ContactPage() {
  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Talk to people who *speak contractor.*"
        lede={contactIntro}
        image={images.teamMeeting}
        imageAlt="The Xeratec team in a meeting"
      >
        <div className="flex flex-wrap gap-3.5">
          <ButtonLink />
          <ButtonLink href={company.phone.href} variant="glass">
            <Phone size={17} aria-hidden="true" />
            {company.phone.display}
          </ButtonLink>
        </div>
      </PageHero>
      <section className="bg-surface px-4 py-16 sm:px-6 md:py-20">
        <div className="mx-auto grid max-w-280 grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-10">
          <div className="on-light rounded-brand border border-line bg-white p-6 shadow-soft sm:p-8 md:p-10">
            <span className="eyebrow text-blue-deep">Send us a message</span>
            <h2 className="font-display mt-3 mb-2 text-[32px] text-slate uppercase md:text-[40px]">
              Tell us what's <em className="accent">eating your time.</em>
            </h2>
            <p className="mb-8 max-w-[56ch] text-muted">
              A few lines is plenty. We'll get back to you promptly — usually
              with a question or two of our own.
            </p>
            <ContactForm />
          </div>
          <ContactDetails />
        </div>
      </section>
      <CtaPanel
        className="bg-white"
        eyebrow="Rather talk it through?"
        title="Grab a time on *our calendar.*"
        text="Skip the back-and-forth. Pick a slot that works for you and we'll come ready to listen."
      />
    </>
  );
}

/**
 * The page's captured HubSpot scheduler, or Jordan's calendar by default.
 * The legacy per-person pages embedded "lunch and learn" schedulers that
 * HubSpot no longer serves ("this page is nowhere to be found"), so those
 * get the default scheduler too.
 */
function bookingUrlFor(page) {
  const embed = page?.externalEmbeds?.find(
    (url) =>
      url.includes("meetings.hubspot.com") && !url.includes("lunch-and-learn"),
  );
  return embed?.replace("?embed=true", "") || defaultBookingUrl;
}

const booking = {
  eyebrow: "Book a Meeting",
  title: "Pick a time that *works for you.*",
  lede: "No pressure and no pitch deck. Choose a time below, tell us a little about your project, and we'll come ready to listen.",
  stepsTitle: "What to expect",
  steps: engagementSteps,
};

/** Schedule a Call; also serves the legacy per-person calendar pages. */
export function BookingPage({ page }) {
  const bookingUrl = bookingUrlFor(page);
  return (
    <>
      <PageHero
        eyebrow={booking.eyebrow}
        title={booking.title}
        lede={booking.lede}
      />
      <section className="bg-surface px-4 py-16 sm:px-6 md:py-20">
        <div className="mx-auto grid max-w-280 grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-10">
          <div>
            <div className="overflow-hidden rounded-brand border border-line bg-white shadow-soft">
              <div className="flex items-center gap-3 border-b border-line px-5 py-4 sm:px-6">
                <CalendarDays
                  size={20}
                  className="text-blue-deep"
                  aria-hidden="true"
                />
                <h2 className="font-display text-lg text-slate uppercase">
                  Choose a time
                </h2>
              </div>
              <iframe
                title="Schedule a meeting with Xeratec"
                src={bookingUrl + "?embed=true"}
                loading="lazy"
                className="h-180 w-full border-0"
              />
            </div>
            <p className="mt-4 text-center text-sm text-muted">
              Calendar not loading?{" "}
              <ExternalLink
                href={bookingUrl}
                className="font-bold text-blue-deep underline underline-offset-2"
              >
                Open it in a new tab
                <span className="sr-only"> (opens in a new tab)</span>
              </ExternalLink>
            </p>
          </div>

          <aside className="flex flex-col gap-6 lg:sticky lg:top-28">
            <div className="rounded-brand border border-line bg-white p-6 shadow-soft">
              <h2 className="eyebrow mb-5 text-blue-deep">
                {booking.stepsTitle}
              </h2>
              <ol className="space-y-5">
                {booking.steps.map(({ title, text }, i) => (
                  <li key={title} className="flex gap-4">
                    <span className="font-display w-7 shrink-0 text-[26px] leading-none text-yellow">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="font-display text-lg text-slate uppercase">
                        {title}
                      </h3>
                      <p className="mt-1 text-[15px] text-muted">{text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <div className="blueprint rounded-brand p-6 text-white">
              <p className="font-display text-xl uppercase">
                Prefer to just call?
              </p>
              <p className="mt-1 mb-4 text-[15px] text-white/80">
                We're happy to talk it through now.
              </p>
              <a
                href={company.phone.href}
                className="flex items-center gap-2 font-bold text-yellow hover:text-yellow-hi"
              >
                <Phone size={17} aria-hidden="true" />
                {company.phone.display}
              </a>
              <a
                href={localHref(routes.contact)}
                className="mt-2 flex items-center gap-2 font-bold text-white/85 hover:text-yellow"
              >
                <Mail size={17} aria-hidden="true" />
                Send us a message
              </a>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
